import {Box, Divider, ListItemButton, Typography} from '@mui/material';
import {t} from 'i18next';
import {useMemo, useRef, useState, useEffect} from 'react';
import './TranscriptListItem.scss';

import ArrTooltip from '../../../../common/arr-tooltip/ArrTooltip';
import ArrMenu, {ArrMenuHandle} from '../../../../common/arr-menu/ArrMenu';
import ArrIconButton from '../../../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as MoreIcon} from '../../../../../assets/images/menu.svg';

import {
  MyTranscriptListMenuKey,
  TranscriptFormValues,
  TranscriptItemDataType,
} from '../../AiInvestigateUtils';
import TranscriptActionModal from '../transcript-actions-modal/TranscriptActionsModal';
import ArrAvatar from '../../../../common/arr-avatar/ArrAvatar';

export interface TranscriptListItemProps {
  item: TranscriptItemDataType;
  index: number;
  focusedId?: number;
  setFocusedId: React.Dispatch<React.SetStateAction<number>>;
  orderedIds: number[];
  focusViaKeyboardRef: React.MutableRefObject<boolean>;
  showAvatar?: boolean;
  selected?: boolean;
  selectedSortKey?: string;
  onClick?: (item: TranscriptItemDataType) => void;
  onDelete?: (id: number) => void;
  onRename?: (
    id: number,
    newName: string,
    newDescription: string
  ) => boolean | Promise<boolean>;
}

const TranscriptListItem: React.FC<TranscriptListItemProps> = ({
  item,
  index,
  focusedId,
  setFocusedId,
  orderedIds,
  focusViaKeyboardRef,
  showAvatar = false,
  selected = false,
  selectedSortKey,
  onClick,
  onDelete,
  onRename,
}) => {
  const isFocused = focusedId === item.transcriptId;
  const avatarSize = 2;
  const menuRef = useRef<ArrMenuHandle | null>(null);

  const nameRef = useRef<HTMLSpanElement | null>(null);
  const descRef = useRef<HTMLSpanElement | null>(null);

  const buttonRef = useRef<HTMLDivElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);

  const [isNameOverflow, setIsNameOverflow] = useState(false);
  const [isDescOverflow, setIsDescOverflow] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<
    MyTranscriptListMenuKey.Remove | MyTranscriptListMenuKey.Edit | null
  >(null);

  const checkNameOverflow = () => {
    setIsNameOverflow(
      nameRef.current
        ? nameRef.current.scrollWidth > nameRef.current.clientWidth
        : false
    );
  };

  const checkDescOverflow = () => {
    setIsDescOverflow(
      descRef.current
        ? descRef.current.scrollHeight > descRef.current.clientHeight
        : false
    );
  };

  useEffect(() => {
    checkNameOverflow();
    checkDescOverflow();
  }, [item]);

  const actionOptions = useMemo(
    () => [
      {
        id: MyTranscriptListMenuKey.Remove,
        label: t('ai.investigate.listItem.menu.optionA'),
        selected: selectedSortKey === MyTranscriptListMenuKey.Remove,
      },
      {
        id: MyTranscriptListMenuKey.Edit,
        label: t('ai.investigate.listItem.menu.optionB'),
        selected: selectedSortKey === MyTranscriptListMenuKey.Edit,
      },
    ],
    [selectedSortKey]
  );

  const handleMenuClick = (anchor: HTMLElement | null) => {
    if (!anchor) return;
    menuRef.current?.open(anchor);
    setIsMenuOpen(true);
  };

  const handleMenuItemClick = (menuItem: {id: string}) => {
    if (menuItem.id === MyTranscriptListMenuKey.Remove) {
      setActiveModal(MyTranscriptListMenuKey.Remove);
    } else if (menuItem.id === MyTranscriptListMenuKey.Edit) {
      setActiveModal(MyTranscriptListMenuKey.Edit);
    }

    menuRef.current?.close();
    setIsMenuOpen(false);
  };

  const renderName = () => {
    const content = (
      <Typography
        ref={nameRef}
        onMouseEnter={checkNameOverflow}
        className="typo-text label2_size transcript-name"
        data-testid={`transcript-list-name-${index}`}
      >
        {item.transcriptName}
      </Typography>
    );

    return isNameOverflow ? (
      <ArrTooltip
        content={item.transcriptName}
        trigger="hover"
        placement="top"
        className="transcript-tooltip"
        labelId={`tooltip-${item.transcriptName}`}
      >
        {content}
      </ArrTooltip>
    ) : (
      content
    );
  };

  const renderDescription = () => {
    if (!item.description) return null;

    const content = (
      <Typography
        ref={descRef}
        onMouseEnter={checkDescOverflow}
        className="typo-text body5_size transcript-description"
        data-testid={`transcript-list-description-${index}`}
      >
        {item.description}
      </Typography>
    );

    return isDescOverflow ? (
      <ArrTooltip
        content={item.description}
        trigger="hover"
        placement="top"
        className="transcript-tooltip"
        labelId={`tooltip-description-${item.indexID}`}
      >
        {content}
      </ArrTooltip>
    ) : (
      content
    );
  };

  useEffect(() => {
    if (isFocused && focusViaKeyboardRef.current) {
      buttonRef.current?.focus();
      focusViaKeyboardRef.current = false;
    }
  }, [isFocused, focusViaKeyboardRef]);

  return (
    <>
      <Box>
        <ListItemButton
          ref={buttonRef}
          className={`transcript-item ${selected ? 'selected' : ''}`}
          tabIndex={isFocused ? 0 : -1}
          disableRipple
          disableTouchRipple
          aria-selected={selected}
          data-testid={`transcript-item-${item.transcriptId}`}
          onClick={(e) => {
            if (isMenuOpen) {
              e.stopPropagation();
              return;
            }
            onClick?.(item);
            setFocusedId(item.transcriptId);
          }}
          onKeyDown={(e) => {
            if (isMenuOpen) {
              e.stopPropagation();
              return;
            }
            if (e.key === 'ArrowDown') {
              e.preventDefault();
              const nextId =
                orderedIds[Math.min(index + 1, orderedIds.length - 1)];
              if (nextId !== undefined && nextId !== focusedId) {
                focusViaKeyboardRef.current = true;
                setFocusedId(nextId);
              }
            } else if (e.key === 'ArrowUp') {
              e.preventDefault();
              const prevId = orderedIds[Math.max(index - 1, 0)];
              if (prevId !== undefined && prevId !== focusedId) {
                focusViaKeyboardRef.current = true;
                setFocusedId(prevId);
              }
            } else if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onClick?.(item);
            }
          }}
        >
          {showAvatar && (
            <ArrAvatar
              name={item.ownerName}
              className="transcript-avatar"
              size={avatarSize}
              dataTestId="transcript-list-item-avatar"
            />
          )}

          <Box className="transcript-content">
            {renderName()}
            {renderDescription()}

            <Box className="transcript-meta-data">
              <Typography
                className="typo-text body5_size transcript-index"
                data-testid={`transcript-list-item-doc-count-${index}`}
              >
                {t('ai.investigate.listItem.index')} : {item.indexName} (
                {Number(item?.indexDocumentCount ?? 0)}{' '}
                {t('ai.investigate.listItem.documents')})
              </Typography>

              <Typography
                className="typo-text body5_size transcript-meta"
                data-testid={`transcript-list-created-date-${index}`}
              >
                {t('ai.investigate.listItem.created')} :{' '}
                {item?.formattedCreatedDate}
              </Typography>

              {showAvatar && (
                <Typography
                  className="typo-text body5_size transcript-meta"
                  data-testid={`transcript-list-owner-name-${index}`}
                >
                  {t('ai.investigate.listItem.owner')} : {item.ownerName}
                </Typography>
              )}
            </Box>
          </Box>

          {!showAvatar && (
            <Box
              className={`arr-menu-container ${isMenuOpen ? 'menu-open' : ''}`}
            >
              <ArrIconButton
                ref={menuButtonRef}
                onClick={(e) => {
                  e.stopPropagation();
                  handleMenuClick(e.currentTarget);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleMenuClick(menuButtonRef.current);
                  }
                }}
                icon={<MoreIcon />}
                ariaLabel={t(
                  'ai.investigate.transcripts.listItem.menu.moreActions'
                )}
                shape="none"
                className="more-icon-button"
                testId="transcript-list-item-more-actions-icon-button"
              />

              <ArrMenu
                id="more-menu"
                ref={menuRef}
                items={actionOptions}
                onItemClick={handleMenuItemClick}
                direction="bottom-left"
                onClose={() => setIsMenuOpen(false)}
              />
            </Box>
          )}
        </ListItemButton>

        <Divider component="li" className="transcript-divider" />
      </Box>
      {!showAvatar && activeModal && (
        <TranscriptActionModal
          open={Boolean(activeModal)}
          onClose={() => setActiveModal(null)}
          selectedTranscript={item}
          mode={activeModal}
          onConfirm={async (data?: TranscriptFormValues) => {
            let shouldCloseModal = true;
            if (activeModal === MyTranscriptListMenuKey.Remove) {
              onDelete?.(item.transcriptId);
            }

            if (
              activeModal === MyTranscriptListMenuKey.Edit &&
              data?.name != null &&
              data?.description != null
            ) {
              const editSucceeded = await onRename?.(
                item.transcriptId,
                data.name,
                data.description
              );
              if (editSucceeded === false) {
                shouldCloseModal = false;
              }
            }

            if (shouldCloseModal) {
              setActiveModal(null);
            }
          }}
        />
      )}
    </>
  );
};

export default TranscriptListItem;
